// KD-3848 — an answer split into Markdown and the ```koob:<kind> blocks the
// model writes for special answers (prompt rule 11), drawn by AnswerBlock.
// Same shapes as the API's answerBlocks.ts, which the evaluation checks; a
// block that fails them is left out rather than shown as raw JSON.

const isText = v => typeof v === 'string' && v.trim() !== '';
const isOptionalText = v => v === undefined || isText(v);
const listOf = (v, item) => Array.isArray(v) && v.every(item);
const isItem = x => isText(x?.item) && isOptionalText(x?.note);
const isOptionalDate = v =>
  v === undefined || (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v));
const isOptionalCount = (v, min) =>
  v === undefined || (Number.isInteger(v) && v >= min);

const SHAPES = {
  inclusions: b =>
    listOf(b.included ?? [], isItem) &&
    listOf(b.excluded ?? [], isItem) &&
    (b.included?.length ?? 0) + (b.excluded?.length ?? 0) > 0,
  facts: b =>
    listOf(b.facts, x => isText(x?.label) && isText(x?.value)) &&
    b.facts.length > 0,
  schedule: b =>
    listOf(b.steps, x => isText(x?.text) && isOptionalText(x?.time)) &&
    b.steps.length > 0,
  rooms: b =>
    listOf(
      b.rooms,
      x =>
        isText(x?.name) &&
        ['capacity', 'beds', 'size'].every(k => isOptionalText(x?.[k])),
    ) && b.rooms.length > 0,
  pricing: b => isText(b.text),
  availability: b =>
    isOptionalDate(b.startAt) &&
    isOptionalDate(b.endAt) &&
    isOptionalCount(b.adults, 1) &&
    isOptionalCount(b.children, 0) &&
    isOptionalCount(b.rooms, 1),
};

const parseBlock = (kind, body) => {
  try {
    const data = JSON.parse(body.trim());
    return data && typeof data === 'object' && SHAPES[kind]?.(data)
      ? data
      : null;
  } catch {
    return null;
  }
};

const OPEN = /```koob:([a-z_]+)[ \t]*/;
// The start of a fence still streaming in: hidden until it is complete.
const PARTIAL_OPEN = /`{1,3}(k(o(o(b(:[a-z_]*)?)?)?)?)?$/;

/**
 * [{ type: 'markdown', text }, { type: 'block', kind, data }]. While the
 * answer streams, an unfinished block comes as { type: 'block', kind,
 * pending: true }.
 */
export function splitAnswer(text, streaming = false) {
  const parts = [];
  const markdown = value => {
    if (value.trim()) parts.push({ type: 'markdown', text: value });
  };
  let rest = text;
  for (;;) {
    const open = OPEN.exec(rest);
    if (!open) {
      markdown(streaming ? rest.replace(PARTIAL_OPEN, '') : rest);
      return parts;
    }
    markdown(rest.slice(0, open.index));
    const after = rest.slice(open.index + open[0].length);
    const close = after.indexOf('```');
    if (close < 0) {
      if (streaming)
        parts.push({ type: 'block', kind: open[1], pending: true });
      return parts;
    }
    const data = parseBlock(open[1], after.slice(0, close));
    if (data) parts.push({ type: 'block', kind: open[1], data });
    rest = after.slice(close + 3);
  }
}

const blockText = ({ kind, data }) => {
  if (kind === 'inclusions') {
    return [
      ...(data.included ?? []).map(x => `+ ${x.item}`),
      ...(data.excluded ?? []).map(x => `- ${x.item}`),
    ].join('\n');
  }
  if (kind === 'facts')
    return data.facts.map(x => `${x.label}: ${x.value}`).join('\n');
  if (kind === 'schedule')
    return data.steps
      .map(x => (x.time ? `${x.time} ${x.text}` : x.text))
      .join('\n');
  if (kind === 'rooms')
    return data.rooms
      .map(x => [x.name, x.capacity, x.beds, x.size].filter(Boolean).join(', '))
      .join('\n');
  // A search form: nothing worth copying.
  if (kind === 'availability') return '';
  return data.text;
};

/** The answer as plain text, for the copy button. */
export const answerText = text =>
  splitAnswer(text)
    .map(part =>
      part.type === 'markdown' ? part.text.trim() : blockText(part),
    )
    .filter(Boolean)
    .join('\n\n');
